import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// KTK-223 — Per-agent MCP server editor for the Agent V2 settings drawer.
//
// Owns a JSON textarea over the `{mcpServers, _disabled}` config blob.
// Hub stores this on the agent row; the runtime's `agent_set_mcp_servers`
// frame is fanned by Hub after persist so the worker reconnects its
// MCPHost between turns. KTK-209's runtime treats this map as the
// per-agent layer on top of the workspace MCP set.
import { useEffect, useRef, useState } from "react";
export function McpServerEditor({ initial, onSave }) {
    const [editMode, setEditMode] = useState(false);
    const [text, setText] = useState(() => JSON.stringify(stripDisabled(initial), null, 2));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(false);
    const textareaRef = useRef(null);
    // Re-seed the textarea when the consumer feeds a new initial blob
    // (e.g. after a deploy that bumps the workspace defaults).
    useEffect(() => {
        if (!editMode) {
            setText(JSON.stringify(stripDisabled(initial), null, 2));
        }
    }, [initial, editMode]);
    const handleEdit = () => {
        setText(JSON.stringify(stripDisabled(initial), null, 2));
        setEditMode(true);
        setError(null);
        setSaved(false);
        setTimeout(() => textareaRef.current?.focus(), 50);
    };
    const handleCancel = () => {
        setEditMode(false);
        setText(JSON.stringify(stripDisabled(initial), null, 2));
        setError(null);
    };
    const handleSave = async () => {
        if (saving)
            return;
        let parsed;
        try {
            parsed = JSON.parse(text);
        }
        catch (err) {
            setError(`Invalid JSON: ${err instanceof Error ? err.message : String(err)}`);
            return;
        }
        if (!parsed.mcpServers || typeof parsed.mcpServers !== "object") {
            setError('Config must have a "mcpServers" object');
            return;
        }
        const disabled = initial._disabled ?? [];
        const toSave = { ...parsed, _disabled: disabled };
        setSaving(true);
        setError(null);
        try {
            await onSave(toSave);
            setEditMode(false);
            setSaved(true);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setSaving(false);
        }
    };
    const servers = initial.mcpServers ?? {};
    const disabledList = initial._disabled ?? [];
    const serverEntries = Object.entries(servers);
    return (_jsxs("div", { style: {
            background: "var(--paper)",
            borderTop: "1px solid var(--hairline)",
            padding: "10px 14px",
            fontFamily: "var(--sans)",
            fontSize: "var(--fs-body)",
            color: "var(--ink)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
        }, children: [_jsxs("div", { style: { display: "flex", alignItems: "baseline", gap: 10, justifyContent: "space-between" }, children: [_jsxs("div", { children: [_jsx("span", { style: { fontFamily: "var(--serif)", fontSize: 14, fontWeight: 500 }, children: "MCP servers" }), _jsx("div", { style: { fontSize: "var(--fs-meta)", color: "var(--ink-3)" }, children: "Per-agent servers extend the workspace set. The runtime reconnects between turns." })] }), !editMode ? (_jsx("button", { type: "button", onClick: handleEdit, style: buttonStyle("ghost"), children: "edit" })) : (_jsxs("div", { style: { display: "flex", gap: 6 }, children: [_jsx("button", { type: "button", onClick: handleCancel, style: buttonStyle("ghost"), children: "cancel" }), _jsx("button", { type: "button", onClick: () => void handleSave(), disabled: saving, style: buttonStyle("primary", saving), children: saving ? "saving…" : "save" })] }))] }), error && (_jsx("div", { style: {
                    fontSize: "var(--fs-meta)",
                    color: "var(--accent-ink)",
                    background: "var(--accent-soft)",
                    padding: "6px 8px",
                    borderRadius: "var(--radius-sm)",
                }, children: error })), saved && !editMode && (_jsx("div", { style: {
                    fontSize: "var(--fs-meta)",
                    color: "var(--ink-2)",
                    padding: "6px 8px",
                    background: "var(--paper-2)",
                    borderRadius: "var(--radius-sm)",
                }, children: "Saved. The runtime reconnects on the next turn." })), editMode ? (_jsx("textarea", { ref: textareaRef, value: text, onChange: (e) => setText(e.target.value), spellCheck: false, style: {
                    minHeight: 220,
                    padding: "8px 10px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid var(--hairline)",
                    background: "var(--paper-2)",
                    color: "var(--ink)",
                    fontFamily: "var(--mono)",
                    fontSize: "var(--fs-code)",
                    resize: "vertical",
                }, onKeyDown: (e) => {
                    if (e.key === "Tab") {
                        e.preventDefault();
                        const ta = e.currentTarget;
                        const start = ta.selectionStart;
                        const end = ta.selectionEnd;
                        setText(text.substring(0, start) + "  " + text.substring(end));
                        setTimeout(() => {
                            ta.selectionStart = ta.selectionEnd = start + 2;
                        }, 0);
                    }
                    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                        e.preventDefault();
                        void handleSave();
                    }
                } })) : serverEntries.length === 0 ? (_jsx("div", { style: { color: "var(--ink-3)", fontSize: "var(--fs-meta)" }, children: "No agent-level MCP servers configured. Click edit to add." })) : (_jsx("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: serverEntries.map(([name, srv]) => (_jsx(ServerRow, { name: name, config: srv, disabled: disabledList.includes(name) }, name))) }))] }));
}
function ServerRow({ name, config, disabled, }) {
    const transport = typeof config.transport === "string" ? config.transport : "stdio";
    const summary = transport === "stdio"
        ? typeof config.command === "string"
            ? config.command +
                (Array.isArray(config.args) ? ` ${config.args.join(" ")}` : "")
            : "(no command)"
        : typeof config.url === "string"
            ? config.url
            : "(no url)";
    return (_jsxs("div", { style: {
            padding: "6px 10px",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
            border: "1px solid var(--hairline)",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            opacity: disabled ? 0.5 : 1,
        }, children: [_jsx("span", { style: { fontFamily: "var(--mono)", fontSize: "var(--fs-code)", color: "var(--ink)" }, children: name }), _jsx("span", { style: {
                    fontSize: 10,
                    padding: "1px 6px",
                    borderRadius: "var(--radius-sm)",
                    background: "var(--paper)",
                    color: "var(--ink-3)",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                }, children: transport }), _jsx("span", { style: {
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "var(--mono)",
                    fontSize: "var(--fs-meta)",
                    color: "var(--ink-3)",
                }, children: summary }), disabled && (_jsx("span", { style: { fontSize: 10, color: "var(--ink-3)", textTransform: "uppercase" }, children: "disabled" }))] }));
}
function stripDisabled(cfg) {
    const { _disabled, ...rest } = cfg;
    void _disabled;
    return rest;
}
function buttonStyle(variant = "ghost", disabled = false) {
    const base = {
        font: "inherit",
        fontSize: "var(--fs-meta)",
        padding: "6px 14px",
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: "1px solid var(--hairline)",
        background: "var(--paper-2)",
        color: "var(--ink-2)",
    };
    if (variant === "primary") {
        base.background = "var(--accent)";
        base.color = "var(--paper)";
        base.borderColor = "var(--accent)";
    }
    return base;
}
//# sourceMappingURL=McpServerEditor.js.map